import { useState, useRef, useCallback } from 'react';

import { useAppContext, notify } from '../../shared';
import { importContactsJson, importContactsCsv } from '../../api';
import type { DuplicateStrategyType } from '../../api';
import type { BulkContactImportResponse } from '../../api';

const JSON_PLACEHOLDER = JSON.stringify(
  {
    contacts: [
      {
        email: 'john@acme.com',
        phone: '+14155551234',
        first_name: 'John',
        last_name: 'Doe',
        company: 'Acme Corp',
        description: 'VP of Engineering',
      },
    ],
    on_duplicate: 'skip',
    auto_start_campaign: false,
  },
  null,
  2
);

export type FormType = {
  campaignId: string;
  duplicateStrategy: DuplicateStrategyType;
};

export const useImport = () => {
  const { meta } = useAppContext();

  const [form, setForm] = useState<FormType>({
    campaignId: '',
    duplicateStrategy: 'skip',
  });
  const { campaignId, duplicateStrategy } = form;

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BulkContactImportResponse | null>(null);

  // CSV upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // JSON text
  const [jsonText, setJsonText] = useState(JSON_PLACEHOLDER);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setResult(null);
  };

  const handleCsvUpload = useCallback(async () => {
    if (!campaignId) return notify.error('Select a campaign first');
    if (!selectedFile) return notify.error('Select a CSV file first');

    setLoading(true);
    setResult(null);
    try {
      const res = await importContactsCsv(
        campaignId,
        { file: selectedFile },
        duplicateStrategy
      );
      setResult(res);
      notify.success(`Imported ${res.created} contacts`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      notify.error(err.message || 'CSV import failed');
    } finally {
      setLoading(false);
    }
  }, [campaignId, selectedFile, duplicateStrategy]);

  const handleJsonImport = useCallback(async () => {
    if (!campaignId) return notify.error('Select a campaign first');

    let payload: any;
    try {
      payload = JSON.parse(jsonText);
    } catch {
      setJsonError('Invalid JSON');
      return;
    }

    if (
      !payload.contacts ||
      !Array.isArray(payload.contacts) ||
      !payload.contacts.length
    ) {
      setJsonError('"contacts" must be a non-empty array');
      return;
    }

    setJsonError(null);
    setLoading(true);
    setResult(null);
    try {
      const res = await importContactsJson(campaignId, {
        contacts: payload.contacts,
        on_duplicate: payload.on_duplicate || duplicateStrategy,
        auto_start_campaign: payload.auto_start_campaign ?? false,
      });
      setResult(res);
      notify.success(`Imported ${res.created} contacts`);
    } catch (err: any) {
      notify.error(err.message || 'JSON import failed');
    } finally {
      setLoading(false);
    }
  }, [campaignId, jsonText, duplicateStrategy]);

  return {
    loading,
    error: jsonError,

    jsonText,
    setJsonText,
    setJsonError,

    campaignId,
    duplicateStrategy,

    form,
    setForm,

    result,
    campaigns: meta.campaigns,

    selectedFile,
    fileInputRef,

    handleCsvUpload,
    handleFileChange,
    handleJsonImport,
  };
};
